/**
 * ResellerDashboard — Dashboard KPI ridotta per utenti reseller/partner.
 *
 * Mostra:
 *   - Conteggio ordini per stato
 *   - Totale importo ordini
 *   - Numero aziende gestite
 *   - Link rapido per creare nuovo ordine
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChannelBadge from './ChannelBadge';

export default function ResellerDashboard({ ordini, aziende, mssp }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const stats = useMemo(() => {
    if (!ordini || !Array.isArray(ordini)) {
      return {
        totaleOrdini: 0,
        ordiniAperti: 0,
        ordiniDaFatturare: 0,
        ordiniAutorinnovo: 0,
        importoTotale: 0,
        aziendeCount: 0,
      };
    }

    const importoTotale = ordini.reduce((sum, o) => {
      const val = parseFloat(o.totale_importo || o.importo_euro || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    return {
      totaleOrdini: ordini.length,
      ordiniAperti: ordini.filter(o => o.stato_ordine === 'APERTO').length,
      ordiniDaFatturare: ordini.filter(o => o.stato_ordine === 'DA FATTURARE').length,
      ordiniAutorinnovo: ordini.filter(o => o.stato_ordine === 'AUTORINNOVO').length,
      importoTotale: importoTotale.toFixed(2),
      aziendeCount: Array.isArray(aziende) ? aziende.length : 0,
    };
  }, [ordini, aziende]);

  return (
    <div className="space-y-6">
      {/* Intestazione con info profilo */}
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">
            {mssp?.nome_mssp || t('reseller.dashboard', 'Dashboard')}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <ChannelBadge channelType={mssp?.tipo_mssp === 'reseller' ? 'RESELLER' : 'ASSOCIAZIONE'} size="md" />
          </div>
        </div>
        <button
          onClick={() => navigate('/ordini/nuovo')}
          className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-4 py-2 rounded-md transition-colors"
        >
          + {t('reseller.nuovoOrdine', 'Nuovo Ordine')}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={t('reseller.totaleOrdini', 'Totale Ordini')}
          value={stats.totaleOrdini}
          color="blue"
        />
        <KpiCard
          title={mssp?.tipo_mssp === 'reseller' ? 'Ordini Da Attivare' : t('reseller.ordiniAperti', 'Ordini Aperti')}
          value={stats.ordiniAperti}
          color="yellow"
        />
        <KpiCard
          title={mssp?.tipo_mssp === 'reseller' ? 'Ordini Attivati' : t('reseller.ordiniDaFatturare', 'Da Fatturare')}
          value={stats.ordiniDaFatturare}
          color="indigo"
        />
        <KpiCard
          title={t('reseller.importoTotale', 'Importo Totale')}
          value={`€ ${stats.importoTotale}`}
          color="green"
        />
      </div>

      {/* Tabella ultimi ordini (preview) */}
      {ordini && ordini.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-semibold text-gray-700">
              {t('reseller.ultimiOrdini', 'Ultimi ordini')}
            </h3>
            <span className="text-xs text-gray-400">
              {stats.aziendeCount} {t('reseller.aziendeGestite', 'aziende gestite')}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Ordine</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Azienda</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Stato</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Totale</th>
                </tr>
              </thead>
              <tbody>
                {ordini.slice(0, 10).map((o) => (
                  <tr key={o.id_ordine} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{o.numero_ordine}</td>
                    <td className="px-4 py-2">{o.azienda_info?.nome_azienda || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        o.stato_ordine === 'APERTO' ? 'bg-yellow-100 text-yellow-800' :
                        o.stato_ordine === 'DA FATTURARE' ? 'bg-blue-100 text-blue-800' :
                        o.stato_ordine === 'AUTORINNOVO' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {mssp?.tipo_mssp === 'reseller' ? (o.stato_ordine === 'APERTO' ? 'DA ATTIVARE' : o.stato_ordine === 'DA FATTURARE' ? 'ATTIVATO' : o.stato_ordine) : o.stato_ordine}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold">{o.totale_importo} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ title, value, color = 'blue' }) {
  const colorMap = {
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    green:  'bg-green-50 text-green-700 border-green-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };

  return (
    <div className={`rounded-lg border p-4 ${colorMap[color] || colorMap.blue}`}>
      <p className="text-xs font-medium opacity-75">{title}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
